import { AssetIdSchema, AssetSchema, ProjectIdSchema } from "@storytell/types";
import z from "zod";

export const AssetIdParamsSchema = z.object({
  id: AssetIdSchema,
});

export const UpdateAssetInputSchema = AssetSchema.pick({
  name: true,
  description: true,
  labels: true,
});

// HTTP API schemas for file upload flow
export const CreateAssetHttpInputSchema = z.object({
  name: z.string(),
  filename: z.string(),
  fileSizeInBytes: z.number(),
  mimeType: z.string(),
  md5: z.string(),
  projectId: z.string().optional(),
  description: z.string().optional(),
});

export const CreateAssetHttpResponseSchema = AssetSchema.extend({
  method: z.enum(["SIGNED_URL", "DIRECT"]),
  signedUrl: z.string().optional(),
});
export type CreateAssetHttpResponse = z.infer<typeof CreateAssetHttpResponseSchema>;

export const UploadCompleteParamsSchema = z.object({
  assetId: AssetIdSchema,
});

export const GetAssetsRequestSchema = z.object({
  assetIds: z.array(AssetIdSchema),
});

export const GetAssetsResponseSchema = z
  .object({
    records: z.array(AssetSchema),
  })
  .loose();

const MoveDestinationSchema = z.object({
  assetId: AssetIdSchema,
  projectId: ProjectIdSchema,
});

export const MoveAssetsRequestSchema = z.object({
  assets: z.array(MoveDestinationSchema),
  moveLabels: z.boolean().optional().default(true),
});

export const CloneAssetRequestSchema = z.object({
  destination: ProjectIdSchema,
  copyLabels: z.boolean().optional().default(true),
});

// URL ingestion schemas
export const IngestURLsRequestSchema = z.object({
  urls: z.array(z.url()).min(1).max(10),
});

const IngestURLErrorSchema = z.object({
  url: z.string(),
  errorMessage: z.string(),
});

export const IngestURLsResponseSchema = z.object({
  assets: z.array(AssetSchema),
  errors: z.array(IngestURLErrorSchema).optional(),
});

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export const SearchAssetsParamsSchema = z.object({
  q: z.string().optional(),
  limit: z.number().int().positive().max(100).optional(),
  type: z.string().optional(),
  owner: z.string().optional(),
  status: z.string().optional(),
  after: z.string().regex(DATE_RE).optional(),
  before: z.string().regex(DATE_RE).optional(),
});
export type SearchAssetsParams = z.infer<typeof SearchAssetsParamsSchema>;

export const SearchAssetsHitSchema = z.object({
  assetId: AssetIdSchema,
  name: z.string(),
  // 1 = matched on name, 2 = matched on description only.
  tier: z.number().int(),
  score: z.number(),
  // Name is highlighted client-side; description is highlighted server-side with literal <mark>
  // spans, present only for tier-2 hits.
  descriptionSnippet: z.string().optional(),
  typeCategory: z.string().optional(),
  mimeType: z.string().optional(),
  createdAt: z.string(),
  modifiedAt: z.string(),
  owner: z.string().optional(),
  lifecycleState: z.string().optional(),
});
export type SearchAssetsHit = z.infer<typeof SearchAssetsHitSchema>;

export const SearchAssetsResponseSchema = z.object({
  hits: z.array(SearchAssetsHitSchema),
});
export type SearchAssetsResponse = z.infer<typeof SearchAssetsResponseSchema>;
